import { ArrowDown, ArrowUp, CheckSquare, Download, FileText, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type GrInvoice, usePendingVistaInvoices, usePostToVistaGr } from "@/hooks/use-gr-invoices";
import { useProjects } from "@/hooks/use-projects";

type SortKey = keyof Pick<
  GrInvoice,
  "vendorNameRaw" | "invoiceNumber" | "invoiceDate" | "invoiceAmount" | "approvedAt"
>;

function formatCurrency(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "--";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function sortValue(inv: GrInvoice, key: SortKey): string | number {
  if (key === "invoiceAmount") return Number(inv.invoiceAmount) || 0;
  if (key === "approvedAt" || key === "invoiceDate")
    return inv[key] ? new Date(inv[key] as string).getTime() : 0;
  return inv[key] ?? "";
}

function exportCsv(rows: GrInvoice[]) {
  const headers = ["Vendor", "Invoice #", "Invoice Date", "Amount", "Approved At"];
  const lines = rows.map((r) =>
    [
      r.vendorNameRaw,
      r.invoiceNumber,
      formatDate(r.invoiceDate),
      r.invoiceAmount,
      formatDate(r.approvedAt),
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(","),
  );
  const csv = [headers.join(","), ...lines].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "gr-pending-vista.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// ── Post to Vista dialog ──

function PostToVistaDialog({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: GrInvoice;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [batchId, setBatchId] = useState("");
  const postMutation = usePostToVistaGr();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[50vw] max-w-none">
        <DialogHeader>
          <DialogTitle>Post to Vista — {invoice.vendorNameRaw}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md bg-muted/50 p-3 text-sm">
            <span className="font-medium">{invoice.invoiceNumber}</span>
            {" · "}
            {formatDate(invoice.invoiceDate)}
            {" · "}
            <span className="font-semibold">{formatCurrency(invoice.invoiceAmount)}</span>
          </div>
          <div className="space-y-2">
            <Label>
              Vista Batch ID <span className="text-destructive">*</span>
            </Label>
            <Input
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              placeholder="e.g. AP-2024-03-001"
            />
            <p className="text-xs text-muted-foreground">
              Enter the A/P batch ID from Viewpoint Vista after manual entry.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              postMutation.mutate(
                { id: invoice.id, vistaBatchId: batchId },
                { onSuccess: () => onOpenChange(false) },
              )
            }
            disabled={!batchId.trim() || postMutation.isPending}
          >
            <CheckSquare className="mr-2 h-4 w-4" />
            {postMutation.isPending ? "Marking posted..." : "Mark as Posted"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Row actions ──

function PostAction({ invoice }: { invoice: GrInvoice }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setOpen(true)}>
        <CheckSquare className="mr-1 h-3 w-3 text-blue-600" />
        Mark Posted
      </Button>
      {open && <PostToVistaDialog invoice={invoice} open={open} onOpenChange={setOpen} />}
    </>
  );
}

// ── Page ──

export function GrPendingVistaPage() {
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("approvedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [sortedColumn, setSortedColumn] = useState<SortKey | null>("approvedAt");

  const { data: projects } = useProjects();
  const {
    data: invoices,
    isLoading,
    isError,
    error,
    refetch,
  } = usePendingVistaInvoices({
    projectId: projectFilter !== "all" ? projectFilter : undefined,
  });

  const allItems = invoices ?? [];

  const projectOptions = useMemo(
    () => (projects ?? []).sort((a, b) => a.name.localeCompare(b.name)),
    [projects],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((inv) =>
      [inv.vendorNameRaw, inv.invoiceNumber, formatDate(inv.invoiceDate)]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [allItems, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (sortedColumn === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortedColumn(key);
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortedColumn !== col) return null;
    return sortDir === "asc" ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    );
  }

  const totalPending = allItems.reduce((acc, inv) => acc + (Number(inv.invoiceAmount) || 0), 0);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load pending Vista invoices"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">GR Pending Vista Entry</h1>
          <p className="text-sm text-muted-foreground">
            Approved invoices waiting to be entered into Viewpoint Vista A/P
          </p>
        </div>
        {allItems.length > 0 && (
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Total Pending</p>
            <p className="text-xl font-bold">{formatCurrency(totalPending)}</p>
            <Badge variant="warning">
              {allItems.length} invoice{allItems.length === 1 ? "" : "s"}
            </Badge>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search vendor, invoice #..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projectOptions.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => exportCsv(sorted)}
          disabled={sorted.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Download CSV
        </Button>
      </div>

      {/* Table */}
      {sorted.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            <p>
              {allItems.length === 0
                ? "No invoices pending Vista entry. All caught up!"
                : "No results match your search."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("vendorNameRaw")}
                >
                  Vendor
                  <SortIcon col="vendorNameRaw" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("invoiceNumber")}
                >
                  Invoice #
                  <SortIcon col="invoiceNumber" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("invoiceDate")}
                >
                  Invoice Date
                  <SortIcon col="invoiceDate" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none text-right"
                  onClick={() => handleSort("invoiceAmount")}
                >
                  Amount
                  <SortIcon col="invoiceAmount" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("approvedAt")}
                >
                  Approved
                  <SortIcon col="approvedAt" />
                </TableHead>
                <TableHead className="w-32">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-medium">{inv.vendorNameRaw}</TableCell>
                  <TableCell>{inv.invoiceNumber}</TableCell>
                  <TableCell>{formatDate(inv.invoiceDate)}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(inv.invoiceAmount)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(inv.approvedAt)}
                  </TableCell>
                  <TableCell>
                    <PostAction invoice={inv} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
